import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    CircularProgress,
} from '@mui/material';

import NewClassWizard from "./NewClassWizard";
import AssignmentWizard from "./AssignmentWizard";
import LinkTableCell from "./LinkTableCell";
import AssignmentPill from "./AssignmentPill";
import { isClassActive } from "./helpers";
import { useAppState } from "./AppStateContext";
import useSortHead from './useSortHead';

const Classes = () => {
    const navigate = useNavigate();
    const { classes, fetchClasses, isLoadingClasses, error } = useAppState();
    const { TableCellSortHead, sortFunction } = useSortHead();
    const [classWizardOpen, setClassWizardOpen] = useState(false);
    const [assignmentWizardOpen, setAssignmentWizardOpen] = useState(false);
    const [classUUID, setClassUUID] = useState(null);
    const [editAssignment, setEditAssignment] = useState(null);

    const handleCloseWizard = (finished) => {
        setClassWizardOpen(false);
        setAssignmentWizardOpen(false);
        if (finished) {
            fetchClasses();
        }
    };

    const openClass = (theClass) => {
        const classId = theClass.uuid, className = theClass.name;
        navigate(`/classes/${classId}`, { state: { className } });
    };

    const createOrEditAssignment = (classUuid, assignment) => {
        setAssignmentWizardOpen(true);
        setClassUUID(classUuid);
        setEditAssignment(assignment);
    };

    const activeClasses = classes.filter(isClassActive);
    const inactiveClasses = classes.filter(c => !isClassActive(c));

    const sortedActive = [...activeClasses].sort(sortFunction);
    const sortedInactive = [...inactiveClasses].sort(sortFunction);

    return (
        <div>
            <h1>Classes</h1>
            <p>Here you’ll find all your classes. To create a new class, click on + ADD CLASS</p>
            <div className="add-row-button">
                <Button
                    variant="text"
                    color="primary"
                    onClick={() => setClassWizardOpen(true)}
                >
                    + ADD CLASS
                </Button>
            </div>
            <TableContainer>
              {error && (
                  <p style={{ color: 'red', padding: '20px' }}>{error}</p>
              )}
              {isLoadingClasses ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                    <CircularProgress />
                  </div>
              ) : (
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCellSortHead field='name'>Group Name</TableCellSortHead>
                            <TableCellSortHead field='level'>Level</TableCellSortHead>
                            <TableCellSortHead field='term'>Academic Term</TableCellSortHead>
                            <TableCellSortHead field='students_count'>No. of Students</TableCellSortHead>
                            <TableCell>Assignment Due</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedActive.map(row => (
                            <TableRow
                                key={row.uuid}
                            >
                                <LinkTableCell onClick={() => openClass(row)}>{row.name}</LinkTableCell>
                                <TableCell>{row.level}</TableCell>
                                <TableCell>{row.term}</TableCell>
                                <TableCell>{row.students_count}</TableCell>
                                <TableCell>
                                    <AssignmentPill
                                        classUuid={row.uuid}
                                        assignment={row.current_assignment}
                                        date
                                        onClick={createOrEditAssignment}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
              )}
            </TableContainer>

            {!isLoadingClasses && sortedInactive.length > 0 && (
                <>
                    <h2>Inactive classes</h2>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCellSortHead field='name'>Group Name</TableCellSortHead>
                                    <TableCellSortHead field='level'>Level</TableCellSortHead>
                                    <TableCellSortHead field='term'>Academic Term</TableCellSortHead>
                                    <TableCellSortHead field='students_count'>No. of Students</TableCellSortHead>
                                    <TableCell>Assignment Due</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedInactive.map(row => (
                                    <TableRow
                                        key={row.uuid}
                                    >
                                        <LinkTableCell onClick={() => openClass(row)}>{row.name}</LinkTableCell>
                                        <TableCell>{row.level}</TableCell>
                                        <TableCell>{row.term}</TableCell>
                                        <TableCell>{row.students_count}</TableCell>
                                        <TableCell>
                                            <AssignmentPill
                                                classUuid={row.uuid}
                                                assignment={row.current_assignment}
                                                date
                                                onClick={createOrEditAssignment}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}

            <NewClassWizard classes={classes} open={classWizardOpen} onClose={handleCloseWizard} />
            <AssignmentWizard open={assignmentWizardOpen} onClose={handleCloseWizard} classUUID={classUUID} assignment={editAssignment} />
        </div>
    );
};

export default Classes;
